/* Channel selection list with checkboxes, thumbnails, and already-imported detection. */

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

interface YouTubeChannel {
  id: string;
  title: string;
  thumbnail?: string | null;
}

interface YouTubeChannelListProps {
  channels: YouTubeChannel[];
  selectedIds: Set<string>;
  existingChannelIds: Set<string>;
  isImporting: boolean;
  onToggle: (channelId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onImport: () => void;
  onCancel: () => void;
}

export function YouTubeChannelList({
  channels,
  selectedIds,
  existingChannelIds,
  isImporting,
  onToggle,
  onSelectAll,
  onClearSelection,
  onImport,
  onCancel,
}: YouTubeChannelListProps) {
  const selectableCount = channels.filter(
    (ch) => !existingChannelIds.has(ch.id),
  ).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {channels.length} channels found
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            disabled={selectedIds.size === selectableCount}
          >
            Select all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            disabled={selectedIds.size === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[320px] -mx-1 px-1">
        <div className="space-y-0.5">
          {channels.map((channel) => {
            const isExisting = existingChannelIds.has(channel.id);
            const isSelected = selectedIds.has(channel.id);

            return (
              <label
                key={channel.id}
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-accent cursor-pointer"
              >
                <Checkbox
                  checked={isExisting || isSelected}
                  disabled={isExisting}
                  onCheckedChange={() => !isExisting && onToggle(channel.id)}
                />
                <Avatar className="size-7 shrink-0">
                  {channel.thumbnail && (
                    <AvatarImage src={channel.thumbnail} alt={channel.title} />
                  )}
                  <AvatarFallback className="text-[10px]">
                    {channel.title.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm truncate flex-1">{channel.title}</span>
                {isExisting && (
                  <Badge variant="secondary" className="text-[10px] shrink-0">
                    Already added
                  </Badge>
                )}
              </label>
            );
          })}
        </div>
      </ScrollArea>

      <div className="flex items-center gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={onImport}
          disabled={selectedIds.size === 0 || isImporting}
          className="flex-1"
        >
          {isImporting && <Loader2 className="size-4 animate-spin" />}
          Import {selectedIds.size} channel{selectedIds.size !== 1 ? "s" : ""}
        </Button>
      </div>
    </div>
  );
}
